// Owned by task 2.6 of the dashboard-redesign spec (Requirement 10).
//
// Reads upcoming meetings (owned by the `meetings` spec) and exposes them
// as `WidgetResult<UpcomingMeetingRow[]>` for `Widget_UpcomingMeetings` to
// consume. This hook is READ-ONLY: no mutation, no server-side aggregation.
//
// Fetch contract (see design.md §Low-Level Design §Fetch contract):
//   - Starts in `{ status: "loading" }`.
//   - On mount and every `scope` / `days` change: an `AbortController`-guarded GET.
//   - On success, each meeting is turned into an `UpcomingMeetingRow` with
//     `daysUntilStart = Math.floor((Date.parse(scheduledFor) - Date.now()) / 86_400_000)`,
//     filtering out any row where `daysUntilStart < 0` (already past) or
//     `daysUntilStart > days` (out of window). Rows are sorted ascending
//     (nearest first) per Requirement 10.1.
//   - Empty state when the endpoint returns `[]` or nothing survives filtering.
//   - Error state carries an `errorId` (from the `X-Error-Id` response header
//     if present, else a locally-generated UUID v4) so the scoped error card
//     can render it verbatim for support (Requirement 14).
//   - `retry` re-invokes the fetch by bumping a retry counter.
//   - On unmount: aborts the in-flight fetch.
//
// Meeting shape: the endpoint is owned by the `meetings` spec — its inner
// shape can evolve independently. We only require `{ id: string, scheduledFor: string }`
// (ISO 8601). Any additional fields returned by the endpoint are passed
// through unmodified so consumers (and later specs) can read them. Rows
// missing a valid `scheduledFor` are silently skipped, not surfaced as an
// error — per the task's defensive-parsing directive.
//
// The `api` client (frontend/src/services/api.ts) already attaches the Clerk
// JWT and the `x-clerk-subdomain` tenant header via interceptors — this hook
// MUST NOT re-implement any of that.

import { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import api from "../../../services/api";
import type {
  BuildingScope,
  Meeting,
  UpcomingMeetingRow,
  WidgetResult,
} from "../types";

/** Default lookahead window (Requirement 10.1: next 60 days). */
const DEFAULT_DAYS = 60;

/** Milliseconds in a whole day — used for the `daysUntilStart` calculation. */
const MS_PER_DAY = 86_400_000;

/** User-safe message shown in the scoped error card. Never leaks server text. */
const USER_SAFE_ERROR_MESSAGE = "Could not load upcoming meetings";

/**
 * Generate a UUID v4. Prefers `globalThis.crypto.randomUUID` when available;
 * falls back to a small `Math.random`-based hex formatter for environments
 * that don't expose the WebCrypto API (older test runners, JSDOM shims, etc.).
 * Never used to authenticate anything — only to correlate a UI error card to
 * a log entry when the server didn't return an `X-Error-Id` header.
 */
function generateLocalErrorId(): string {
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  // RFC 4122 v4 shape: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx where y ∈ {8,9,a,b}.
  const rand = (): number => Math.floor(Math.random() * 16);
  const hex = (n: number): string => n.toString(16);
  let out = "";
  for (let i = 0; i < 36; i += 1) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      out += "-";
    } else if (i === 14) {
      out += "4";
    } else if (i === 19) {
      out += hex((rand() & 0x3) | 0x8);
    } else {
      out += hex(rand());
    }
  }
  return out;
}

/**
 * Extract an `X-Error-Id` header from an axios error's response. Case-
 * insensitive per RFC 7230; different transports normalise headers
 * differently (Node http lowercases everything, JSDOM leaves them as-is).
 */
function extractErrorIdFromAxios(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const response = (err as AxiosError).response;
  if (!response || !response.headers) return undefined;
  const headers = response.headers as
    | Record<string, string | undefined>
    | { get?: (key: string) => string | null };
  // Some axios versions expose headers as an AxiosHeaders instance with a
  // `get(key)` method; others expose a plain record. Handle both defensively.
  if (typeof (headers as { get?: unknown }).get === "function") {
    const raw = (headers as { get: (key: string) => string | null }).get(
      "x-error-id",
    );
    if (typeof raw === "string" && raw.length > 0) return raw;
  }
  const record = headers as Record<string, string | undefined>;
  const candidate =
    record["x-error-id"] ??
    record["X-Error-Id"] ??
    record["X-ERROR-ID"];
  return typeof candidate === "string" && candidate.length > 0
    ? candidate
    : undefined;
}

/**
 * Minimum contract we require from each meeting object returned by the
 * `meetings` spec endpoint. Extra fields are permitted and preserved
 * unchanged when the row is built.
 */
interface MinimumMeetingShape {
  id: string;
  scheduledFor: string;
}

/**
 * Validate that a single meeting object has the required shape and a
 * parsable ISO 8601 `scheduledFor`. Returns the raw object typed with the
 * minimum contract on success, or `undefined` on any structural failure
 * (missing id, missing/malformed date, non-object row, etc.). Rows that
 * fail here are skipped by the caller — not surfaced as an error, per the
 * task's defensive-parsing directive.
 */
function parseMeeting(row: unknown): MinimumMeetingShape | undefined {
  if (!row || typeof row !== "object") return undefined;
  const r = row as Record<string, unknown>;
  if (typeof r.id !== "string" || r.id.length === 0) return undefined;
  if (typeof r.scheduledFor !== "string" || r.scheduledFor.length === 0) {
    return undefined;
  }
  const parsed = Date.parse(r.scheduledFor);
  if (!Number.isFinite(parsed)) return undefined;
  // Return the ORIGINAL object typed with the minimum contract so all extra
  // fields the endpoint sent through survive intact.
  return row as MinimumMeetingShape;
}

/**
 * Compute the whole-day distance between `scheduledFor` and `now` using the
 * exact formula from the task spec: `Math.floor((Date.parse(scheduledFor) -
 * Date.now()) / 86_400_000)`. Assumes `scheduledFor` was already validated
 * by `parseMeeting`.
 */
function computeDaysUntilStart(scheduledFor: string, now: number): number {
  return Math.floor((Date.parse(scheduledFor) - now) / MS_PER_DAY);
}

export function useUpcomingMeetings(
  scope: BuildingScope,
  days: number = DEFAULT_DAYS,
): WidgetResult<UpcomingMeetingRow[]> {
  const [retryCounter, setRetryCounter] = useState(0);
  const [result, setResult] = useState<WidgetResult<UpcomingMeetingRow[]>>({
    status: "loading",
  });

  const retry = useCallback(() => {
    setRetryCounter((n) => n + 1);
  }, []);

  const scopeKind = scope.kind;
  const scopeBuildingId = scope.kind === "single" ? scope.buildingId : null;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    // Reset to loading on scope/retry/days change so consumers see the
    // skeleton between fetches instead of stale ready data.
    setResult({ status: "loading" });

    const buildingId =
      scopeKind === "all" ? "ALL" : (scopeBuildingId ?? "ALL");
    const url =
      `/meetings/upcoming?days=${encodeURIComponent(String(days))}` +
      `&buildingId=${encodeURIComponent(buildingId)}`;

    api
      .get(url, { signal: controller.signal })
      .then((response) => {
        if (cancelled) return;

        const body = response?.data;
        if (!Array.isArray(body)) {
          setResult({
            status: "error",
            errorId: generateLocalErrorId(),
            message: "Unexpected response shape",
            retry,
          });
          return;
        }

        // Freeze `Date.now()` inside this callback so every row in the
        // response is compared against the same reference instant. This
        // also matches the deterministic day math tested with fake timers.
        const now = Date.now();

        const rows: UpcomingMeetingRow[] = [];
        for (const raw of body) {
          const parsed = parseMeeting(raw);
          if (!parsed) continue; // silently skip malformed rows
          const daysUntilStart = computeDaysUntilStart(
            parsed.scheduledFor,
            now,
          );
          // Filter out past meetings and anything beyond the requested window.
          if (daysUntilStart < 0) continue;
          if (daysUntilStart > days) continue;
          rows.push({
            // The full object flows through unmodified — the endpoint (owned
            // by the `meetings` spec) is the source of truth for the shape.
            // The cast bridges the shared `Meeting` type (which the design's
            // `UpcomingMeetingRow` references) to the endpoint's live payload.
            meeting: parsed as unknown as Meeting,
            daysUntilStart,
          });
        }

        if (rows.length === 0) {
          setResult({ status: "empty" });
          return;
        }

        // Sort ascending by `daysUntilStart` (nearest first) per Req 10.1.
        rows.sort((a, b) => a.daysUntilStart - b.daysUntilStart);

        setResult({ status: "ready", data: rows });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The browser/axios raises `CanceledError` (or a `DOMException`
        // named `AbortError`) when the AbortController fires. That path is
        // the caller's intent, not a failure — swallow silently.
        if (controller.signal.aborted) return;
        const errorId =
          extractErrorIdFromAxios(err) ?? generateLocalErrorId();
        setResult({
          status: "error",
          errorId,
          message: USER_SAFE_ERROR_MESSAGE,
          retry,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `scope.kind`, `scope.buildingId`, and `days` are the only inputs;
    // `retryCounter` triggers a re-fetch on `retry()`. `retry` and `api`
    // are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKind, scopeBuildingId, days, retryCounter]);

  return result;
}
